import React from "react";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { IconButton, toast } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { ViewIcon, ViewOffIcon } from "@hugeicons-pro/core-stroke-rounded";
import { UPDATE_COMPANY } from "graphql/company";
import { isAllocationTypeHidden } from "hooks/useAllocationTypes";
import type { AllocationType } from "hooks/useAllocationTypes";

interface AllocationTypeVisibilityToggleProps {
  allocationType: AllocationType;
  onChanged: () => void;
}

/** Hides or shows a whole type; a type a custom field collects stays as it is. */
const AllocationTypeVisibilityToggle: React.FC<
  AllocationTypeVisibilityToggleProps
> = ({ allocationType, onChanged }) => {
  const { t } = useTranslation();
  const [updateCompany, { loading }] = useMutation(UPDATE_COMPANY);

  const toggle = async () => {
    const visible = !allocationType.visible;
    try {
      // The settings merge by key, and keep the stored choice of a type a custom field collects.
      await updateCompany({
        variables: {
          id: "",
          input: {
            settings: {
              ALLOCATION_TYPES: [{ key: allocationType.key, visible }],
            },
          },
        },
      });
      onChanged();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      toast.error(t("Failed to update allocation visibility") + ": " + message);
    }
  };

  const linkedField = allocationType.isLinkedCustomField;
  const label = linkedField
    ? t("CustomFieldsMustBeVisible")
    : allocationType.visible
      ? t("Hide")
      : t("Show");

  return (
    <Tooltip title={label} placement="top">
      {/* The span carries the tooltip while the button is disabled. */}
      <span>
        <IconButton
          variant="ghost"
          aria-label={label}
          disabled={!!linkedField || loading}
          onClick={toggle}
        >
          <HugeiconsIcon
            icon={
              isAllocationTypeHidden(allocationType) ? ViewOffIcon : ViewIcon
            }
            size={18}
          />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default AllocationTypeVisibilityToggle;
